import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import BrowserHelper from 'src/app/helpers/browser-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import Asset from 'src/app/model/asset';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import { IP_BOARD_PART_TYPE_CHOICES } from 'src/app/model/product/ip-board/ip-board-part-type';
import { BaseProject } from 'src/app/model/project/base-project';
import { Project } from 'src/app/model/project/project';
import { SignShapeTypesChoices } from 'src/app/model/sign/enums/sign-shape-type';
import { SignTypesChoices } from 'src/app/model/sign/enums/sign-type';
import {
  ProjectDisplayObservableService,
  SignSelectedEvent,
} from 'src/app/services/project-display-observable.service';
import { ClientSignSelectorHelper } from 'src/app/shared/project-components/client-project-form/base-project/client-ip-board-form/client-sign-selector/client-sign-selector-helper';
import { ClientIPBoardAndPlacementHelper } from 'src/app/shared/project-components/client-project-form/base-project/client-ip-board-form/helper/client-ip-board-and-placement-helper';
import { ClientIPBoardFormHelper } from 'src/app/shared/project-components/client-project-form/base-project/client-ip-board-form/helper/client-ip-board-form-helper';
import { ClientIPBoardPartFormHelper } from 'src/app/shared/project-components/client-project-form/base-project/client-ip-board-form/helper/client-ip-board-part-form-helper';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { FileHelper } from '../../../../../../../helpers/file-helper';

@Component({
  selector: 'client-ip-board-summary',
  templateUrl: './client-ip-board-summary.component.html',
  styleUrls: ['./client-ip-board-summary.component.scss'],
})
export class ClientIPBoardSummaryComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @Input()
  ipBoard: IPBoard;
  @Input()
  project: Project;
  @Input()
  baseProject: BaseProject;
  @Input()
  client: boolean = false;
  @Input()
  multiple: boolean = false;
  @Input()
  index: number;

  @Output()
  save = new EventEmitter<any>();

  isImage = FileHelper.isImage;
  totalStock: number;
  doTitleSidesDiffer: boolean = false;
  doPhoneNumberSidesDiffer: boolean = false;
  displayExampleStyle: any;

  boardTypePreviewUrl: string;
  placementTypePreviewUrl: string;

  safari: boolean = false;

  onHoldSectionCollapsed: boolean = true;
  onHoldCheckboxModel: boolean = false;

  showDesignSection: boolean = false;
  designSectionCollapsed: boolean = true;

  SIGN_TYPE_CHOICES = SignTypesChoices;
  SIGN_SHAPE_TYPE_CHOICES = SignShapeTypesChoices;
  IP_BOARD_PART_TYPE_CHOICES = IP_BOARD_PART_TYPE_CHOICES;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    super();
  }

  ngOnInit(): void {
    this.safari = BrowserHelper.isSafari();
    this.onHoldSectionCollapsed = !this.baseProject.onHold;
    this.onHoldCheckboxModel = !this.baseProject.onHold;
    this.calculateDesignSectionState();
    this.calculateDoSidesDiffer();
    this.calculateBoardTypePreviewUrl();
    this.calculatePlacementTypePreviewUrl();
    this.calculateDisplayExampleStyle();

    this.addSubscription(
      this.projectDisplayObservableService.getSignSelectedSubscribable(),
      (event: SignSelectedEvent) => {
        if (
          ClientIPBoardFormHelper.matchesEvent(this.ipBoard.boardPart, event)
        ) {
          this.calculateDisplayExampleStyle();
        }
      }
    );

    this.addSubscription(
      this.projectDisplayObservableService.getIPBoardConfigurationChangedSubscribable(),
      () => {
        this.calculateBoardTypePreviewUrl();
        this.calculatePlacementTypePreviewUrl();
        this.calculateDoSidesDiffer();
        this.calculateDisplayExampleStyle();
      }
    );

    this.addSubscription(
      this.projectDisplayObservableService.getSubProjectSetOnHoldSubscribable(),
      (id: number) => {
        if (id === this.baseProject.id) {
          this.onHoldSectionCollapsed = false;
          this.onHoldCheckboxModel = false;
          if (
            this.baseProject.projectPlacement.appointment ||
            this.baseProject.projectPlacement.collectKey
          ) {
            this.baseProject.projectPlacement.firstAppointment = false;
          }
        }
      }
    );

    this.addSubscription(
      this.projectDisplayObservableService.getProductDesignVariableChangedSubscribable(),
      () => {
        this.calculateDesignSectionState();
      }
    );
  }

  private calculateDesignSectionState(): void {
    this.showDesignSection = this.ipBoard.needsDesign;
    this.designSectionCollapsed = !(
      this.ipBoard.needsDesign && !this.ipBoard.designFinished
    );
  }

  private calculateDoSidesDiffer(): void {
    this.doTitleSidesDiffer = ProductHelper.doTitleSidesDiffer(
      this.ipBoard.boardPart
    );
    this.doPhoneNumberSidesDiffer = ProductHelper.doPhoneNumberSidesDiffer(
      this.ipBoard.boardPart
    );
  }

  private calculateBoardTypePreviewUrl(): void {
    if (
      this.ipBoard.boardPart != null &&
      ClientIPBoardAndPlacementHelper.isAdhesiveBoard(this.ipBoard.boardPart)
    ) {
      const adhesiveBoardOption =
        ClientIPBoardPartFormHelper.getInitialOptionAdhesive(
          this.ipBoard.boardPart
        );
      this.boardTypePreviewUrl =
        adhesiveBoardOption == null ? null : adhesiveBoardOption.previewUrl;
    } else {
      this.boardTypePreviewUrl = null;
    }
  }

  private calculatePlacementTypePreviewUrl(): void {
    if (this.ipBoard.boardPart == null) {
      this.placementTypePreviewUrl = null;
      return;
    }
    const selectedPlacementOption =
      ClientIPBoardAndPlacementHelper.isAdhesiveBoard(this.ipBoard.boardPart)
        ? ClientIPBoardAndPlacementHelper.getInitialSelectedAdhesiveBoardPlacementOption(
            this.ipBoard.boardPart
          )
        : ClientIPBoardAndPlacementHelper.getInitialSelectedIPBoardPlacementOption(
            this.ipBoard.boardPart
          );
    this.placementTypePreviewUrl =
      selectedPlacementOption == null
        ? null
        : selectedPlacementOption.previewUrl;
  }

  private calculateDisplayExampleStyle(): void {
    const searchTarget = this.getExampleSearchTarget();
    const displayExample = CollectionHelper.isPopulated(searchTarget)
      ? searchTarget.find((e) => e != null && !e.archived)
      : null;
    this.displayExampleStyle =
      displayExample == null
        ? null
        : ClientSignSelectorHelper.getBackgroundImageStyle(
            `/api/assets/${displayExample.asset.uuid}`
          );
  }

  private getExampleSearchTarget(): ExampleCandidate[] {
    if (this.ipBoard.boardPart.sticker != null) {
      return this.ipBoard.boardPart.sticker.examples;
    }
    if (this.ipBoard.boardPart.sign != null) {
      return this.ipBoard.boardPart.sign.examples;
    }
    return null;
  }

  onHoldReasonChanged(value: string): void {
    this.baseProject.onHoldReason = value;
    this.save.emit();
  }

  onHoldHandlingChanged(value: string): void {
    this.baseProject.onHoldHandling = value;
    this.save.emit();
  }

  onHoldCheckboxModelChanged(): void {
    this.baseProject.onHold = !this.onHoldCheckboxModel;
    this.save.emit();
  }
}

interface ExampleCandidate {
  asset: Asset;
  archived: boolean;
}
